import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { EmployeeOption } from "../hooks/useEmployees.js";
import { useCurrentUser } from "../hooks/useAuth.js";
import type { EngagementRef } from "../hooks/useEngagements.js";
import {
  useCreateEngagement,
  useDeleteEngagement,
  useSetEngagementTarget,
  useUpdateEngagement,
} from "../hooks/useEngagements.js";
import { useZielrunde } from "../hooks/useKpi.js";
import { JiraAssetPanel } from "./JiraAssetPanel.js";

/**
 * Verwaltungs-Karte „Servicebeauftragungen" im Kundendetail.
 *
 * Pro (Kunde × Segment) können mehrere Beauftragungen existieren (Splits wie
 * HCM- vs. SuccessFactors-Service). Der Lead der Beauftragung ist Tool-owned:
 * der Sync setzt ihn nur beim Auto-Seed, danach gehört er dem BL.
 */

interface SegmentRef {
  id: string;
  name: string;
}

export function EngagementsCard({
  customerId,
  segments,
  engagements,
  employees,
  canEdit,
}: {
  customerId: string;
  segments: SegmentRef[];
  engagements: EngagementRef[];
  employees: EmployeeOption[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const createEngagement = useCreateEngagement(customerId);
  const [addingSegmentId, setAddingSegmentId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");

  const employeeOptions = employees.map((e) => ({
    value: e.id,
    label: e.fullName,
  }));

  if (segments.length === 0) {
    return <Text c="dimmed">{t("pages.customers.detail.noSegments")}</Text>;
  }

  return (
    <Stack gap="sm">
      {segments.map((seg) => {
        const segEngagements = engagements.filter(
          (e) => e.segmentId === seg.id,
        );
        return (
          <Box key={seg.id}>
            <Group gap="xs" mb={6}>
              <Badge variant="light" color="blue" size="md">
                {seg.name}
              </Badge>
              {canEdit && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => {
                    setAddingSegmentId(
                      addingSegmentId === seg.id ? null : seg.id,
                    );
                    setNewName("");
                  }}
                >
                  {t("pages.customers.detail.addEngagement")}
                </Button>
              )}
            </Group>

            {addingSegmentId === seg.id && (
              <Group gap="xs" mb="xs">
                <TextInput
                  size="xs"
                  placeholder={t(
                    "pages.customers.detail.engagementNamePlaceholder",
                  )}
                  value={newName}
                  onChange={(e) => setNewName(e.currentTarget.value)}
                  style={{ minWidth: 240 }}
                />
                <Button
                  size="compact-xs"
                  disabled={newName.trim().length === 0}
                  loading={createEngagement.isPending}
                  onClick={() =>
                    createEngagement.mutate(
                      { segmentId: seg.id, name: newName.trim() },
                      { onSuccess: () => setAddingSegmentId(null) },
                    )
                  }
                >
                  {t("common.save")}
                </Button>
              </Group>
            )}

            {segEngagements.length === 0 ? (
              <Text c="dimmed" size="sm">
                {t("pages.customers.detail.noEngagements")}
              </Text>
            ) : (
              <Stack gap="xs">
                {segEngagements.map((eng) => (
                  <EngagementRow
                    key={eng.id}
                    customerId={customerId}
                    engagement={eng}
                    employeeOptions={employeeOptions}
                    canEdit={canEdit}
                  />
                ))}
              </Stack>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function EngagementRow({
  customerId,
  engagement,
  employeeOptions,
  canEdit,
}: {
  customerId: string;
  engagement: EngagementRef;
  employeeOptions: Array<{ value: string; label: string }>;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  /**
   * Der Service Lead pflegt seine EIGENE Beauftragung auch hier.
   *
   * Bis zum 2026-08-20 kannte diese Karte nur „darf alles" oder „darf nichts",
   * waehrend das Lead-Cockpit ihm Stellvertreter, Umsetzer und Ziel anbot —
   * dasselbe Objekt, zwei Antworten. Der Server liess es ohnehin zu
   * (`updateEngagement`: der aktuelle Lead darf, Name und Lead-Wechsel bleiben
   * der Segment-Fuehrung). Jetzt sagt die Oberflaeche an beiden Stellen
   * dasselbe.
   */
  const istEigenerLead =
    engagement.leadEmployeeId !== null &&
    engagement.leadEmployeeId === user?.employeeId;
  const darfPflegen = canEdit || istEigenerLead;
  /**
   * Diese Karte kennt die Zielrunde, weil sie — anders als das Lead-Cockpit —
   * einen zweiten Schreibweg zu `setEngagementTarget` ist: dieselbe
   * Beauftragung, dasselbe Zielfeld, dieselbe Servergrenze. Ohne diese Prüfung
   * bot die Karte das Feld nach dem Einfrieren weiter zum Bearbeiten an; der
   * Server lehnte still ab (403 für den Lead, 400 für die Bereichsleitung
   * ohne Begründung), aber der globale Mutations-Handler meldet nur Erfolge —
   * für den Nutzer passierte sichtbar nichts. Das Jahr ist bewusst das
   * laufende, nicht `engagement.targetYear` (identisch, aber die Zielrunde
   * ist an das Kalenderjahr gebunden, nicht an das Beauftragungsfeld).
   */
  const { data: zielrunde } = useZielrunde(
    engagement.segmentId,
    new Date().getFullYear(),
  );
  const zielrundeEingefroren = zielrunde?.status === "eingefroren";
  const updateEngagement = useUpdateEngagement(customerId);
  const deleteEngagement = useDeleteEngagement(customerId);
  const setTarget = useSetEngagementTarget(customerId);

  const [name, setName] = useState(engagement.name);
  const [revenueTarget, setRevenueTarget] = useState<number | string>(
    engagement.revenueTargetEur !== null
      ? Number(engagement.revenueTargetEur)
      : "",
  );
  const [growthTarget, setGrowthTarget] = useState<number | string>(
    engagement.growthTargetPct !== null
      ? Number(engagement.growthTargetPct)
      : "",
  );

  const prevYear =
    engagement.previousYearRevenueEur !== null &&
    Number(engagement.previousYearRevenueEur) > 0
      ? Number(engagement.previousYearRevenueEur)
      : null;

  const pending =
    updateEngagement.isPending ||
    deleteEngagement.isPending ||
    setTarget.isPending;

  /**
   * F4 — hängt die Beauftragung an einer Jira-Kundenabteilung, führen die
   * Assets Name und Service Lead. Beide werden dann nicht zum Bearbeiten
   * angeboten, sondern als „aus Jira" gekennzeichnet: Eine Eingabe hier
   * überschriebe der nächtliche Lauf wieder, und der Fehler fiele erst am
   * nächsten Tag auf. Der eigentliche Riegel sitzt serverseitig — das hier ist
   * die Erklärung dazu, nicht der Schutz.
   *
   * Stellvertreter, Umsetzer und Ziele bleiben auch dann CRM-eigen: Eine
   * Stellvertretung kennt das Asset gar nicht.
   */
  const jiraManaged = engagement.jiraAssetId !== null;
  /**
   * Verknüpft, aber noch nie übernommen. Der Bootstrap stellt nur die
   * Verknüpfung her; Name und Lead holt erst der nächtliche Lauf. Dazwischen
   * stünde sonst ein leeres, gesperrtes Feld mit dem Abzeichen „aus Jira" —
   * eine Behauptung, die weder hier noch in Jira einen Weg lässt.
   */
  const jiraNochNichtUebernommen =
    jiraManaged && engagement.jiraAssetSyncedAt === null;
  const jiraLabel = jiraNochNichtUebernommen
    ? t("pages.customers.detail.engagementFromJiraPending")
    : t("pages.customers.detail.engagementFromJira");
  const jiraTitel = jiraNochNichtUebernommen
    ? t("pages.customers.detail.engagementFromJiraPendingHint")
    : undefined;

  if (!darfPflegen) {
    // Read-only-Sicht (z. B. Projektleiter bei fremder Beauftragung).
    return (
      <Paper withBorder p="xs">
        <Group gap="md" wrap="wrap">
          <Text fw={600} size="sm">
            {engagement.name}
          </Text>
          <Text size="sm">
            {t("pages.customers.detail.engagementLead")}:{" "}
            {engagement.leadEmployeeName ?? t("common.dashed")}
          </Text>
          {engagement.deputyEmployeeName && (
            <Text size="sm" c="dimmed">
              {t("pages.customers.detail.engagementDeputy")}:{" "}
              {engagement.deputyEmployeeName}
            </Text>
          )}
        </Group>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="xs">
      <Stack gap={6}>
        <Group gap="xs" wrap="wrap">
          {jiraManaged ? (
            <Group gap={6}>
              <Text fw={600} size="sm">
                {engagement.name}
              </Text>
              <Badge
                variant={jiraNochNichtUebernommen ? "outline" : "light"}
                color={jiraNochNichtUebernommen ? "orange" : "teal"}
                size="sm"
                title={jiraTitel}
              >
                {jiraLabel}
              </Badge>
            </Group>
          ) : !canEdit ? (
            <Text fw={600} size="sm">
              {engagement.name}
            </Text>
          ) : (
            <TextInput
              size="xs"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              onBlur={() => {
                const trimmed = name.trim();
                if (trimmed && trimmed !== engagement.name) {
                  updateEngagement.mutate({
                    engagementId: engagement.id,
                    name: trimmed,
                  });
                } else {
                  setName(engagement.name);
                }
              }}
              disabled={pending}
              style={{ minWidth: 220 }}
              aria-label={t("pages.customers.detail.engagementName")}
            />
          )}
          <Badge variant="light" color="gray" size="sm">
            {t("pages.customers.detail.engagementProjects", {
              count: engagement.activeProjectCount,
            })}
          </Badge>
          {canEdit && (
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            title={t("pages.customers.detail.engagementDelete")}
            aria-label={t("pages.customers.detail.engagementDelete")}
            onClick={() =>
              deleteEngagement.mutate({ engagementId: engagement.id })
            }
            disabled={pending}
          >
            ✕
          </ActionIcon>
          )}
          {deleteEngagement.isError && (
            <Text c="red" size="xs">
              {t("pages.customers.detail.engagementDeleteBlocked")}
            </Text>
          )}
        </Group>

        <Group gap="xs" wrap="wrap">
          {jiraManaged ? (
            <Group gap={6}>
              <Text size="sm">
                {t("pages.customers.detail.engagementLead")}:{" "}
                {engagement.leadEmployeeName ?? t("common.dashed")}
              </Text>
              <Badge
                variant={jiraNochNichtUebernommen ? "outline" : "light"}
                color={jiraNochNichtUebernommen ? "orange" : "teal"}
                size="sm"
                title={jiraTitel}
              >
                {jiraLabel}
              </Badge>
            </Group>
          ) : !canEdit ? (
            <Text size="sm">
              {t("pages.customers.detail.engagementLead")}:{" "}
              {engagement.leadEmployeeName ?? t("common.dashed")}
            </Text>
          ) : (
            <Select
              size="xs"
              placeholder={t("pages.customers.detail.engagementLead")}
              value={engagement.leadEmployeeId}
              data={employeeOptions}
              searchable
              clearable
              onChange={(v) =>
                updateEngagement.mutate({
                  engagementId: engagement.id,
                  leadEmployeeId: v,
                })
              }
              disabled={pending}
              style={{ minWidth: 200 }}
            />
          )}
          <Select
            size="xs"
            placeholder={t("pages.customers.detail.engagementDeputy")}
            value={engagement.deputyEmployeeId}
            data={employeeOptions}
            searchable
            clearable
            onChange={(v) =>
              updateEngagement.mutate({
                engagementId: engagement.id,
                deputyEmployeeId: v,
              })
            }
            disabled={pending}
            style={{ minWidth: 200 }}
          />
          <MultiSelect
            size="xs"
            placeholder={t("pages.customers.detail.engagementImplementers")}
            value={engagement.implementers.map((i) => i.employeeId)}
            data={employeeOptions}
            searchable
            onChange={(ids) =>
              updateEngagement.mutate({
                engagementId: engagement.id,
                implementerIds: ids,
              })
            }
            disabled={pending}
            style={{ minWidth: 240, flex: 1 }}
          />
        </Group>

        <Group gap="xs" wrap="wrap" align="flex-end">
          {/* Vorjahresumsatz = Bezugsgröße der gekoppelten Zielpflege:
              €-Ziel und Wachstums-% rechnen sich gegenseitig um. */}
          <Box>
            <Text size="xs" c="dimmed">
              {t("pages.customers.detail.engagementPrevYear", {
                year: engagement.targetYear - 1,
              })}
            </Text>
            <Text size="sm" fw={600} mb={4}>
              {prevYear !== null
                ? `${Math.round(prevYear).toLocaleString("de-DE")} €`
                : t("common.dashed")}
            </Text>
          </Box>
          {zielrundeEingefroren ? (
            // Zielrunde eingefroren: das Feld wird angezeigt, nicht mehr zur
            // Bearbeitung angeboten — Änderungen mit Begründung laufen über
            // das Lead-Cockpit bzw. den Ziele-Reiter, nicht über diese Karte.
            <Box>
              <Text
                size="xs"
                c="dimmed"
                title={t("pages.customers.detail.engagementTargetFrozenHint")}
              >
                {t("pages.customers.detail.engagementTarget", {
                  year: engagement.targetYear,
                })}
              </Text>
              <Text size="sm" fw={600} mb={4}>
                {engagement.revenueTargetEur !== null
                  ? `${Math.round(Number(engagement.revenueTargetEur)).toLocaleString("de-DE")} €`
                  : t("common.dashed")}
              </Text>
            </Box>
          ) : (
            <>
              <NumberInput
                size="xs"
                label={t("pages.customers.detail.engagementTarget", {
                  year: engagement.targetYear,
                })}
                value={revenueTarget}
                onChange={(v) => {
                  setRevenueTarget(v);
                  if (prevYear && v !== "" && Number(v) >= 0) {
                    setGrowthTarget(
                      Math.round((Number(v) / prevYear - 1) * 1000) / 10,
                    );
                  }
                }}
                min={0}
                step={1000}
                suffix=" €"
                style={{ width: 180 }}
              />
              <NumberInput
                size="xs"
                label={t("pages.customers.detail.engagementGrowthTarget")}
                value={growthTarget}
                onChange={(v) => {
                  setGrowthTarget(v);
                  if (prevYear && v !== "") {
                    setRevenueTarget(
                      Math.round(prevYear * (1 + Number(v) / 100)),
                    );
                  }
                }}
                min={-100}
                step={0.5}
                suffix=" %"
                disabled={!prevYear}
                title={
                  !prevYear
                    ? t("pages.customers.detail.engagementNoPrevYear")
                    : undefined
                }
                style={{ width: 140 }}
              />
              <Button
                size="compact-xs"
                variant="light"
                loading={setTarget.isPending}
                onClick={() =>
                  setTarget.mutate({
                    engagementId: engagement.id,
                    year: engagement.targetYear,
                    revenueTargetEur:
                      revenueTarget === "" ? null : Number(revenueTarget),
                    growthTargetPct:
                      growthTarget === "" ? null : Number(growthTarget),
                  })
                }
              >
                {t("pages.customers.detail.engagementSaveTarget")}
              </Button>
            </>
          )}
        </Group>

        {/* Verknuepfung mit der Jira-Kundenabteilung (F1) — ohne sie bleiben
            die Ticketbloecke des Statusberichts leer. */}
        <JiraAssetPanel engagementId={engagement.id} customerId={customerId} />
      </Stack>
    </Paper>
  );
}
